// Cola de feedbacks para el profe: lista de sus clases completed con estado del feedback.
// Click "Rellenar" / "Editar" abre el drawer.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import { Spinner } from '@/components/atoms/Spinner';
import { Header } from '@/components/organisms/Header';
import { FeedbackEditorDrawer } from '@/components/organisms/FeedbackEditorDrawer';
import { ApiError } from '@/services/api';
import { listTeacherQueue, type TeacherFeedbackQueueItem } from '@/services/feedbacks';
import styles from './ProfeFeedbacks.module.scss';

type FilterKey = 'all' | 'pending' | 'complete';

function formatDate(iso: string): string {
  const d = new Date(iso);
  const day = d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
  const time = d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  return `${day.charAt(0).toUpperCase() + day.slice(1)} · ${time}`;
}

export function ProfeFeedbacks() {
  const [items, setItems] = useState<TeacherFeedbackQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterKey>('pending');

  const [editing, setEditing] = useState<TeacherFeedbackQueueItem | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listTeacherQueue();
      setItems(rows);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  const filtered = useMemo(() => {
    if (filter === 'all') return items;
    if (filter === 'complete') return items.filter((it) => it.is_complete === 1);
    return items.filter((it) => it.is_complete !== 1);
  }, [items, filter]);

  const pendingCount = items.filter((it) => it.is_complete !== 1).length;
  const completeCount = items.filter((it) => it.is_complete === 1).length;

  return (
    <>
      <Header title="Feedbacks de mis clases" />
      <main className={styles.main}>
        <p className={styles.lead}>
          Aquí tienes la lista de tus clases ya completadas. Rellena el feedback de cada alumno y márcalo como completo para que reciba el aviso.
        </p>

        <div className={styles.tabs} role="tablist">
          <button
            type="button"
            className={`${styles.tab} ${filter === 'pending' ? styles.tabActive : ''}`}
            onClick={() => setFilter('pending')}
          >
            Pendientes ({pendingCount})
          </button>
          <button
            type="button"
            className={`${styles.tab} ${filter === 'complete' ? styles.tabActive : ''}`}
            onClick={() => setFilter('complete')}
          >
            Completados ({completeCount})
          </button>
          <button
            type="button"
            className={`${styles.tab} ${filter === 'all' ? styles.tabActive : ''}`}
            onClick={() => setFilter('all')}
          >
            Todos ({items.length})
          </button>
        </div>

        {loading ? (
          <div className={styles.center}><Spinner size="lg" /></div>
        ) : error ? (
          <p className={styles.error}>{error}</p>
        ) : filtered.length === 0 ? (
          <div className={styles.empty}>
            <Icon name="star" size={32} />
            <h3>{filter === 'pending' ? '¡Estás al día!' : 'Sin resultados'}</h3>
            <p>
              {filter === 'pending'
                ? 'No tienes feedbacks pendientes ahora mismo.'
                : 'Cambia de pestaña para ver otros estados.'}
            </p>
          </div>
        ) : (
          <ul className={styles.list}>
            {filtered.map((it) => (
              <li key={`${it.booking_id}-${it.student_id}`} className={styles.row}>
                <div className={styles.rowDate}>{formatDate(it.scheduled_at)}</div>
                <div className={styles.rowBody}>
                  <div className={styles.rowHeader}>
                    <span className={styles.studentName}>{it.student_name ?? it.student_email}</span>
                    {it.lesson_title && <span className={styles.lessonTitle}>· {it.lesson_title}</span>}
                  </div>
                  <div className={styles.rowMeta}>
                    <Badge tone={it.booking_type === 'gr' ? 'accent' : 'neutral'}>
                      {it.booking_type === 'gr' ? 'Grupal' : 'Individual'}
                    </Badge>
                    {it.feedback_id === null ? (
                      <Badge tone="warning">Sin rellenar</Badge>
                    ) : it.is_complete === 1 ? (
                      <Badge tone="success">Completado</Badge>
                    ) : (
                      <Badge tone="neutral">Borrador</Badge>
                    )}
                  </div>
                </div>
                <div className={styles.rowActions}>
                  <Button variant="secondary" size="sm" onClick={() => setEditing(it)}>
                    {it.feedback_id === null ? 'Rellenar' : it.is_complete === 1 ? 'Ver / editar' : 'Continuar'}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>

      {editing && (
        <FeedbackEditorDrawer
          open={true}
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
          bookingId={editing.booking_id}
          studentId={editing.student_id}
          studentName={editing.student_name ?? editing.student_email}
          lessonTitle={editing.lesson_title}
          scheduledAt={editing.scheduled_at}
          onSaved={() => {
            setEditing(null);
            void reload();
          }}
        />
      )}
    </>
  );
}
